//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import SolutionCard from '../card/solutionCard';
import Test from '../../icons/test.svg';
import Plan from '../../icons/plan.svg';
import Deploy from '../../icons/deploy.svg';
import Velocity from '../../icons/velocity.svg';
import Code from '../../icons/DevOpsAutomationCode.svg';
import Control from '../../icons/DevOpsControl.svg';
import Build from '../../icons/DevOpsAutomationBuild.svg';
import AppScan from '../../icons/modern/appscan_logo.png';
import Harbor from '../../icons/modern/harbor_logo.png';
import { useConfig } from '../../Config';
import { getRoutePath, solutionsApp } from '../../routeUtils';
import { useTranslation } from 'react-i18next';
import { infoOptionsStyles } from '../../styles/infoOptionsStyles';
import { useUserAuth } from '../../UserAuth';

function Solutions() {
  const { t } = useTranslation();
  const { solutions, baseName } = useConfig();
  const { licenses } = useUserAuth();
  const LicenseApp = licenses.flatMap((license) =>
    license.split(',').map((item: string) => item.trim().toLowerCase()),
  );

  const getExternalPath = (name: string): string | undefined => {
    if (Array.isArray(solutions)) {
      const solution = (solutions as solutionsApp[]).find(
        (app) => app.name === name,
      );
      return solution ? solution.path : undefined;
    }
    return undefined;
  };

  // Solutions that live on a separate subdomain and must open directly
  // instead of being embedded in an iframe.
  const externalSolutions = ['Harbor'];

  const renderSolutionCard = (
    title: string,
    text: string,
    svg: string | undefined,
    routeName: string,
    defaultButtonText: string,
    defaultButtonLink: string,
  ) => {
    const isExternal = externalSolutions.includes(routeName);
    const routePath = getRoutePath(solutions as solutionsApp[], routeName);
    const buttonLink = isExternal
      ? getExternalPath(routeName) ?? defaultButtonLink
      : routePath
        ? `${baseName}${routePath}`
        : defaultButtonLink;
    const isAvailable = LicenseApp.includes(routeName.toLowerCase());
    const buttonText = isAvailable
      ? t("Let's go to ") + routeName
      : defaultButtonText + routeName;

    return (
      <div
        style={infoOptionsStyles.cardStyle}
        id={`id-solution-card-${routeName.toLowerCase()}`}
        data-testid={`solution-card-${routeName.toLowerCase()}`}
      >
        <SolutionCard
          title={title}
          text={text}
          svgContent={
            <img
              src={svg ?? 'default.svg'}
              alt={`${title} SVG`}
              style={{ paddingLeft: '16px' }}
            />
          }
          buttonText={buttonText}
          buttonLink={buttonLink}
          isButtonDisabled={!isAvailable}
          isExternal={isExternal}
        />
      </div>
    );
  };

  return (
    <div
      style={{ marginLeft: '50px', marginTop: '35px' }}
      id="id-solutions-section"
      data-testid="solutions-section"
    >
      <h1 style={{ ...infoOptionsStyles.spanStyle, textAlign: 'left' }}>
        {t('solution')}
      </h1>
      <div
        style={{
          marginTop: '16px',
          ...infoOptionsStyles.cardContainerStyle,
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
        }}
      >
        {renderSolutionCard(
          'Plan',
          t('planDescription'),
          Plan,
          'Plan',
          t('Purchase '),
          'https://www.ibm.com/devops',
        )}
        {renderSolutionCard(
          'Control',
          t('controlDescription'),
          Control,
          'Control',
          t('Purchase '),
          'https://www.ibm.com/devops',
        )}
        {renderSolutionCard(
          'Deploy',
          t('deployDescription'),
          Deploy,
          'Deploy',
          t('Purchase '),
          'https://www.ibm.com/devops',
        )}
        {renderSolutionCard(
          'Test',
          t('testDescription'),
          Test,
          'Test',
          t('Purchase '),
          'https://www.ibm.com/devops',
        )}
        {renderSolutionCard(
          'Measure',
          t('valueStreamDescription'),
          Velocity,
          'Measure',
          t('Purchase '),
          'https://www.ibm.com/devops',
        )}
        {renderSolutionCard(
          'Release',
          t('releaseDescription'),
          Velocity,
          'Release',
          t('Purchase '),
          'https://www.ibm.com/devops',
        )}
        {solutions &&
          Array.isArray(solutions) &&
          solutions.find(
            (solution: any) => solution.name === 'Code' && solution.show,
          ) &&
          renderSolutionCard(
            'Code',
            t('codeDescription'),
            Code,
            'Code',
            t('Purchase '),
            'https://www.ibm.com/devops',
          )}
        {solutions &&
          Array.isArray(solutions) &&
          solutions.find(
            (solution: any) => solution.name === 'Build' && solution.show,
          ) &&
          renderSolutionCard(
            'Build',
            t('Build-Description'),
            Build,
            'Build',
            t('Purchase '),
            'https://www.ibm.com/devops',
          )}
        {solutions &&
          Array.isArray(solutions) &&
          solutions.find(
            (solution: any) => solution.name === 'AppScan' && solution.show,
          ) &&
          renderSolutionCard(
            'AppScan',
            t('appScanDescription'),
            AppScan,
            'AppScan',
            t('Purchase '),
            'https://www.ibm.com/devops',
          )}
        {solutions &&
          Array.isArray(solutions) &&
          solutions.find(
            (solution: any) => solution.name === 'Harbor' && solution.show,
          ) &&
          renderSolutionCard(
            'Harbor',
            t('harborDescription'),
            Harbor,
            'Harbor',
            t('Purchase '),
            'https://www.ibm.com/devops',
          )}
      </div>
    </div>
  );
}

export default Solutions;
